// G2.12 — Push opt-in status row (admin dashboard port).
//
// Mirrors apps/pos/src/components/features/notifications/PushStatusRow.tsx
// visually and behaviourally. Kept in the admin tree so both apps can own
// their own design-token imports without a cross-app dependency.

import { Spinner } from '@bbpos/ui';
import { GLASS_FORM, cn, toneClasses, type Tone } from '@/constants/design-tokens';
import type { EnablePushStatus } from '@/lib/push-notifications';

export type PushUiState = 'idle' | 'pending' | EnablePushStatus | 'lost';

type StateTone = 'success' | 'warning' | 'danger';

interface CopyEntry {
  tone: StateTone;
  title: string;
  description: string;
  iconPath: string;
  retryable: boolean;
}

const COPY: Record<Exclude<PushUiState, 'idle' | 'pending'>, CopyEntry> = {
  subscribed: {
    tone: 'success',
    title: 'Browser push is active on this device',
    description: 'OS notifications will appear even when Orio360 is in the background.',
    iconPath: 'M5 13l4 4L19 7',
    retryable: false,
  },
  denied: {
    tone: 'danger',
    title: 'Browser blocked push notifications',
    description: 'Enable them in your browser settings to receive alerts.',
    iconPath: 'M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L5.636 5.636',
    retryable: false,
  },
  unsupported: {
    tone: 'warning',
    title: 'Browser push is unsupported on this device',
    description: 'Try a recent version of Chrome, Edge, Firefox, or Safari.',
    iconPath: 'M12 9v2m0 4h.01M4.93 4.93l14.14 14.14',
    retryable: false,
  },
  'no-vapid': {
    tone: 'warning',
    title: 'Push is not configured on the server',
    description: 'Ask your administrator to set the VAPID keys.',
    iconPath: 'M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    retryable: false,
  },
  error: {
    tone: 'danger',
    title: 'Could not enable browser push',
    description: 'Toggle the switch off and on to retry.',
    iconPath: 'M12 8v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z',
    retryable: true,
  },
  lost: {
    tone: 'warning',
    title: 'Push subscription was lost',
    description: 'Toggle off and on to re-enable.',
    iconPath: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9',
    retryable: true,
  },
};

const TONE_MAP: Record<StateTone, Tone> = {
  success: 'success',
  warning: 'warning',
  danger: 'danger',
};

function accentBarClass(tone: StateTone): string {
  if (tone === 'success') return 'before:bg-emerald-500';
  if (tone === 'warning') return 'before:bg-amber-500';
  return 'before:bg-rose-500';
}

function titleClass(tone: StateTone): string {
  if (tone === 'success') return 'text-emerald-700 dark:text-emerald-300';
  if (tone === 'warning') return 'text-amber-700 dark:text-amber-300';
  return 'text-rose-700 dark:text-rose-300';
}

function retryButtonClass(tone: StateTone): string {
  if (tone === 'warning') {
    return 'text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/30';
  }
  return 'text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-900/30';
}

interface PushStatusRowProps {
  state: PushUiState;
  errorDetail?: string;
  onRetry?: () => void;
}

export function PushStatusRow({ state, errorDetail, onRetry }: PushStatusRowProps) {
  if (state === 'idle') return null;

  if (state === 'pending') {
    return (
      <div
        className={cn(
          GLASS_FORM,
          'mt-2 px-3.5 py-2.5 flex items-center gap-2.5',
          "relative overflow-hidden before:content-[''] before:absolute before:inset-y-0 before:left-0 before:w-0.5 before:bg-sky-500",
        )}
        role="status"
        aria-live="polite"
        data-testid="push-status-row"
        data-state="pending"
      >
        <Spinner variant="inline" size="sm" />
        <span className="text-xs text-neutral-600 dark:text-neutral-300 tabular-nums">
          Asking the browser for permission…
        </span>
      </div>
    );
  }

  const copy = COPY[state];
  const description = state === 'error' && errorDetail ? errorDetail : copy.description;
  const halo = toneClasses(TONE_MAP[copy.tone]);

  return (
    <div
      className={cn(
        GLASS_FORM,
        'mt-2 px-3.5 py-2.5 flex items-start gap-3',
        "relative overflow-hidden before:content-[''] before:absolute before:inset-y-0 before:left-0 before:w-0.5",
        accentBarClass(copy.tone),
      )}
      role="status"
      aria-live="polite"
      data-testid="push-status-row"
      data-state={state}
    >
      <div
        className={cn(
          'shrink-0 w-8 h-8 rounded-lg flex items-center justify-center',
          halo.icon,
        )}
        aria-hidden="true"
      >
        <svg
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d={copy.iconPath} />
        </svg>
      </div>
      <div className="min-w-0 flex-1">
        <div className={cn('text-xs font-semibold leading-tight', titleClass(copy.tone))}>
          {copy.title}
        </div>
        <div className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-0.5 tabular-nums">
          {description}
        </div>
      </div>
      {copy.retryable && onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className={cn(
            'shrink-0 self-center text-[11px] font-semibold px-2.5 py-1 rounded-lg transition-colors tabular-nums',
            retryButtonClass(copy.tone),
          )}
          data-testid="push-status-retry"
        >
          Retry
        </button>
      )}
    </div>
  );
}
